import type { ReactNode } from "react";
import { cn } from "@bindery/ui/lib/utils";
import { MICRO_CAPS } from "./type";

/**
 * The transaction ledger — the return page's centrepiece, and the reason it
 * needs no spinner.
 *
 * A shopper watching this page has exactly one question ("did my money arrive,
 * and is something still happening?"), and the honest answer has two parts
 * that the monorepo's single spinner could not tell apart: whether Stripe has
 * taken the payment, and whether the signed receipt has been issued for it.
 * The poll knows both — see `SessionPoll.lastState` — so the page says both,
 * one row each, and the moment the webhook lands the PAYMENT row flips to
 * "received" while the shopper is still watching.
 *
 * Set as a receipt: a micro-caps key, a dotted leader, a monospace value. The
 * leader is the contents-page device apps/retells uses for its own dotted
 * rules (see the `--rule` token's note in its index.css), one of the threads
 * tying this payments surface to the books ecosystem it belongs to.
 */
export function Ledger({ children }: { readonly children: ReactNode }) {
  return <dl className="flex flex-col gap-3">{children}</dl>;
}

/** How settled one row's value is — same three tones as the status rail. */
export type RowTone = "quiet" | "settled" | "held";

const VALUE: Record<RowTone, string> = {
  quiet: "text-muted-foreground",
  settled: "text-verdigris",
  held: "text-ochre",
};

export function LedgerRow({
  label,
  value,
  tone = "quiet",
  delayMs = 0,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: RowTone;
  /** Stagger, so the rows print rather than appear. */
  readonly delayMs?: number;
}) {
  return (
    <div
      className="animate-print-in flex items-baseline gap-3"
      style={{ animationDelay: `${String(delayMs)}ms` }}
    >
      <dt className={cn(MICRO_CAPS, "text-muted-foreground shrink-0")}>
        {label}
      </dt>
      {/* The leader lives inside the <dd> because `dl > div` may contain only
          dt and dd — a bare span between them would be invalid markup that
          assistive technology is free to interpret however it likes. */}
      <dd className="flex min-w-0 flex-1 items-baseline gap-3">
        {/* A zero-baseline flex child aligns its bottom edge to the row's
            baseline, which is exactly where a leader belongs. */}
        <span aria-hidden className="text-rule leader h-[1.5px] flex-1" />
        <span
          className={cn("shrink-0 font-mono text-xs tabular-nums", VALUE[tone])}
        >
          {value}
        </span>
      </dd>
    </div>
  );
}
